import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { CANTON_NAMES } from "@/lib/cantons";
import { ArrowRight } from "lucide-react";

export default function Cantons() {
  const { t } = useLang();
  const { data: cantons = [], isLoading } = useQuery({
    queryKey: ["cantonsAll"],
    queryFn: async () => (await api.get("/cantons", { params: { include_empty: true } })).data,
  });

  const jsonLd = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: "Événements par canton — Suisse",
    description: "Parcourez les événements médiévaux, vikings, païens et folk rituels de chaque canton suisse.",
  };

  return (
    <>
      <Seo title={t("cantons_title")} description={t("cantons_subtitle")} jsonLd={jsonLd} canonicalPath="/cantons" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-10 border-b border-amber-900/30 pb-5">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">swiss-pagan.ch</div>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("cantons_title")}</h1>
          <p className="text-stone-400 max-w-3xl mt-4">{t("cantons_subtitle")}</p>
        </div>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(9)].map((_, i) => <div key={i} className="h-[86px] bg-[#121418] border border-[#272A34] rounded-lg animate-pulse" />)}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cantons-grid">
            {cantons.map((c, i) => (
              <Link
                key={c.key}
                to={`/canton/${c.key}`}
                data-testid={`canton-index-${c.key}`}
                className="group flex items-center gap-4 bg-[#121418] hover:bg-[#181A20] border border-[#272A34] hover:border-amber-500/50 rounded-lg p-5 transition-all duration-300 hover:-translate-y-0.5 fade-up"
                style={{ animationDelay: `${Math.min(i * 35, 500)}ms` }}
              >
                <span className="w-12 h-12 rounded-xl bg-amber-950/40 border border-amber-800/40 flex items-center justify-center shrink-0 font-serif text-amber-400">{c.key}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-stone-100 group-hover:text-amber-100 truncate">{CANTON_NAMES[c.key] || c.key}</div>
                  <div className="font-mono text-xs text-stone-500">{c.count} {t("results_count")}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-600 group-hover:text-amber-400 group-hover:translate-x-1 transition-all shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
